import React from "react";
import { useTranslation } from "react-i18next";
import { Atom } from "lucide-react";
import { quantumDisplays, quantumRevealState } from "@/lib/decisionGraphLegend";

// The six required Quantum-state displays + the verified-lift REVEAL. Honest by
// construction: the reveal lights up ONLY when a held-out-confirmed lift Evidence
// record backs it. A development-only signal shows "Development signal only" and
// the absence of any lift Evidence shows "Run with no lift" — never a fabricated
// reveal or metric.
const REVEAL_KEY = { verified_lift: "verified", development_signal: "development", no_lift: "none" };

export default function QuantumContributionPanel({ summary, evidence }) {
  const { t } = useTranslation();
  const reveal = quantumRevealState(summary, evidence);
  const displays = quantumDisplays(summary, evidence);
  const verified = reveal.state === "verified_lift";
  const bannerKey = REVEAL_KEY[reveal.state];
  const tileValue = (d) => {
    if (d.key === "sampler_state") {
      const k = `decisionGraph.quantum.samplerStates.${d.value}`;
      const label = t(k);
      return label === k ? d.value : label;
    }
    return d.value;
  };
  return (
    <div data-testid="quantum-contribution-panel">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[var(--qn-foreground-subtle)] font-semibold mb-2">
        <Atom className={`w-3.5 h-3.5 ${verified ? "text-[var(--qn-success)]" : ""}`} />{t("decisionGraph.quantum.title")}
      </div>
      <div
        data-testid="quantum-reveal-state"
        data-reveal={reveal.state}
        className={`rounded-lg border px-2.5 py-2 mb-2 text-xs font-semibold ${
          verified
            ? "border-[var(--qn-success)] bg-[color-mix(in_srgb,var(--qn-success)_14%,transparent)] text-[var(--qn-success)]"
            : "border-[var(--qn-border)] bg-[var(--qn-surface)] text-[var(--qn-foreground-subtle)]"
        }`}
      >
        {t(`decisionGraph.quantum.reveal.${bannerKey}`)}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {displays.map((d) => (
          <div key={d.key} className="rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)] px-2.5 py-2" data-testid={`quantum-${d.key}`}>
            <div className="text-[10px] text-[var(--qn-foreground-subtle)]">{t(`decisionGraph.quantum.${d.key}`)}</div>
            <div className={`text-xs font-semibold ${d.gated ? "text-[var(--qn-foreground-subtle)]" : "text-[var(--qn-foreground)]"}`}>{tileValue(d)}</div>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-[var(--qn-foreground-subtle)] mt-2" data-testid="quantum-reveal-note">{t(`decisionGraph.quantum.reveal.${bannerKey}Note`)}</p>
    </div>
  );
}
